import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Collapse, Typography, Space, Switch, Button, Segmented, Empty, Spin, Tag, Tooltip, Popconfirm, Alert, Tabs } from 'antd';
import { ReloadOutlined, UndoOutlined, ExperimentOutlined, SendOutlined, StopOutlined, ClearOutlined, UserDeleteOutlined, VideoCameraOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useAdminGathering, useNodePeople, useServerClock } from '../../hooks/use_gathering';
import {
    planRooms, describeRoom, newSeed, sameRooms, topicFor, roomPairs, countRepeats, pickSeed,
    flowPosition, parseServerTime, gatheringCallUrl,
} from '../../utils/gathering';
import { SAMPLE_GROUPS, SAMPLE_MEMBERSHIPS, SAMPLE_PEOPLE, SAMPLE_TOPICS, SAMPLE_FLOW } from '../../utils/gathering_sample';
import GroupEditor from './GroupEditor';
import GroupOverlap from './GroupOverlap';
import IdealRoom from './IdealRoom';
import TopicEditor from './TopicEditor';
import FlowEditor from './FlowEditor';
import RoomBoard, { TRAY } from './RoomBoard';
import ConfigShare from './ConfigShare';
import StartChooser from './StartChooser';
import GatheringStatus from './GatheringStatus';
import GuestAccess from './GuestAccess';
import { useNodeInfo } from '../../hooks/use_node_info';
import { buildGatheringConfig, isBlankSetup } from '../../utils/gathering_config';

const { Text } = Typography;

const SAMPLE_KEY = 'gathering.sample';

const SAMPLE_SOURCE = {
    room_size: 5,
    pool_mode: 'groups',
    groups: SAMPLE_GROUPS,
    memberships: SAMPLE_MEMBERSHIPS,
    participants: SAMPLE_PEOPLE,
    rooms: [],
    published_at: null,
    flow_started_at: null,
    server_now: null,
};

const fromServerRooms = rooms => (rooms || []).map((r, index) => ({
    index, personIds: r.person_ids, quota: r.quota || {}, topic: r.topic ?? null, hangoutRoomName: r.hangout_room_name ?? null,
}));

// Local flow steps carry a stable `key` (React key, draft key) alongside the
// server `id`, which a new step only gets once the flow has been saved.
const toLocalFlow = (flow, sample) => flow.map(s => ({ ...s, key: sample ? `sample-${s.id}` : `step-${s.id}` }));

const publishedLabel = at => new Date(parseServerTime(at)).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/**
 * One numbered admin section — open by default, folds away like Guests
 * (GuestAccess.jsx). Its header controls act without folding it: clicks and
 * key presses stop at them rather than reaching the header's toggle.
 */
function Section({ step, title, extra, children }) {
    const stop = e => e.stopPropagation();
    return (
        <Collapse
            className="gathering-section"
            defaultActiveKey={['section']}
            items={[{
                key: 'section',
                label: <span><span className="gathering-step">{step}</span>{title}</span>,
                extra: extra && <span onClick={stop} onKeyDown={stop}>{extra}</span>,
                children,
            }]}
        />
    );
}

/**
 * Admin view: the flow first (it's what runs the gathering), then the groups
 * and who picked them, the ideal room, the topics, and the rooms of every
 * breakout round — reshuffle, drag, publish, then run the flow.
 *
 * Settings (room size, pool, seats per group, the flow and each round's seed)
 * save as they change, so every admin sees the same plan. Rooms are drafts
 * until published: `drafts[stepKey]` is absent while a round simply follows
 * its plan, and holds a concrete line-up once the admin moves someone or
 * opens a published gathering. Changing the ideal room drops every draft (the
 * rooms were graded against the old ideal); new members joining doesn't —
 * they wait in each round's tray.
 */
function AdminView({ paletteColor }) {
    const navigate = useNavigate();
    const [sample, setSample] = useState(() => {
        try { return localStorage.getItem(SAMPLE_KEY) === '1'; } catch { return false; }
    });
    const admin = useAdminGathering({ enabled: !sample });
    const { nodeName, featureGatheringGuestAccess } = useNodeInfo();
    // Sample topics are editable, in memory only — so topics can be tried
    // out before writing real ones. Reset whenever sample mode is switched on.
    const [sampleTopics, setSampleTopics] = useState(SAMPLE_TOPICS);
    // A sample run lives in this browser: start, pause and extensions.
    const [sampleStartedAt, setSampleStartedAt] = useState(null);
    const [samplePausedAt, setSamplePausedAt] = useState(null);
    const [sampleExtended, setSampleExtended] = useState({});
    const sampleSource = useMemo(() => ({ ...SAMPLE_SOURCE, topics: sampleTopics }), [sampleTopics]);
    const src = sample ? sampleSource : admin.data;

    const [size, setSize] = useState(5);
    const [poolMode, setPoolMode] = useState('groups');
    const [seats, setSeats] = useState({});
    const [flow, setFlow] = useState([]);
    const [includePending, setIncludePending] = useState(false);
    const [drafts, setDrafts] = useState({});
    const [activeRound, setActiveRound] = useState(null);
    const [highlight, setHighlight] = useState(null);
    const [publishing, setPublishing] = useState(false);
    // Chose "design from scratch" on a blank gathering — show the editors
    // even though nothing has been designed yet.
    const [scratch, setScratch] = useState(false);
    const [statusSaving, setStatusSaving] = useState(false);
    // The Groups card's Refresh (refreshGroups below).
    const [refreshingGroups, setRefreshingGroups] = useState(false);

    // Take settings, the flow and the published line-ups from the source once
    // per source (first load, or toggling sample) — after that local state
    // leads and the server follows, so a save landing can't undo a keystroke.
    const loadedFor = useRef(null);
    useEffect(() => {
        const key = sample ? 'sample' : admin.data ? 'live' : null;
        if (!key || loadedFor.current === key) return;
        loadedFor.current = key;
        const localFlow = toLocalFlow(sample ? SAMPLE_FLOW : src.flow, sample);
        setSize(src.room_size);
        setPoolMode(src.pool_mode);
        setSeats(Object.fromEntries(src.groups.map(g => [g.id, g.seats_per_room])));
        setFlow(localFlow);
        const byStep = {};
        (src.rooms || []).forEach(r => { (byStep[r.step_id] = byStep[r.step_id] || []).push(r); });
        setDrafts(Object.fromEntries(localFlow.filter(s => byStep[s.id]).map(s => [s.key, fromServerRooms(byStep[s.id])])));
        setActiveRound(null);
        setHighlight(null);
    }, [sample, admin.data, src]);

    // Groups can come and go after load (created, deleted, another admin):
    // adopt the server's seats for any group we don't know yet.
    const groups = useMemo(() => src?.groups ?? [], [src]);
    useEffect(() => {
        setSeats(prev => {
            const missing = groups.filter(g => prev[g.id] === undefined);
            return missing.length ? { ...prev, ...Object.fromEntries(missing.map(g => [g.id, g.seats_per_room])) } : prev;
        });
    }, [groups]);

    const toggleSample = (on) => {
        try { localStorage.setItem(SAMPLE_KEY, on ? '1' : '0'); } catch { /* keep in memory */ }
        loadedFor.current = null; // re-read settings from whichever source is next
        if (on) { setSampleTopics(SAMPLE_TOPICS); setSampleStartedAt(null); setSamplePausedAt(null); setSampleExtended({}); }
        setSample(on);
    };

    // --- saving (live only) --------------------------------------------------
    const { updateSettings, updateGroup, replaceFlow } = admin;
    const pendingSettings = useRef({});
    const saveSettings = useMemo(() => debounce(() => {
        const patch = pendingSettings.current;
        pendingSettings.current = {};
        updateSettings(patch).catch(() => {});
    }, 400), [updateSettings]);
    const queueSettings = useCallback(patch => {
        if (sample) return;
        pendingSettings.current = { ...pendingSettings.current, ...patch };
        saveSettings();
    }, [sample, saveSettings]);
    const seatTimers = useRef({});
    const queueSeats = (gid, n) => {
        if (sample) return;
        clearTimeout(seatTimers.current[gid]);
        seatTimers.current[gid] = setTimeout(() => updateGroup(gid, { seats_per_room: n }).catch(() => {}), 400);
    };
    useEffect(() => () => saveSettings.flush(), [saveSettings]);

    // The flow saves as a whole. saveFlowNow() is also what publish waits on,
    // so every breakout round has a server id before its rooms are sent.
    const flowRef = useRef(flow);
    flowRef.current = flow;
    const flowDirty = useRef(false);
    const flowTimer = useRef(null);
    const saveFlowNow = useCallback(() => {
        clearTimeout(flowTimer.current);
        if (sample || !flowDirty.current) return Promise.resolve(null);
        flowDirty.current = false;
        const steps = flowRef.current;
        return replaceFlow(steps.map(s => ({ id: s.id, kind: s.kind, duration_minutes: s.duration_minutes, seed: s.seed })))
            .then(d => {
                // Adopt the ids of steps the server just created (same order).
                setFlow(f => f.map((s, i) => (s.id ? s : { ...s, id: d.flow[i]?.id })));
                return d;
            });
    }, [sample, replaceFlow]);
    const changeFlow = next => {
        setFlow(next);
        if (sample) return;
        flowDirty.current = true;
        clearTimeout(flowTimer.current);
        flowTimer.current = setTimeout(() => saveFlowNow().catch(() => {}), 600);
    };
    useEffect(() => () => clearTimeout(flowTimer.current), []);

    // --- people, groups, pool ------------------------------------------------
    const { people: nodePeople, loading: peopleLoading } = useNodePeople({ enabled: !sample && poolMode === 'everyone', includePending });
    const peopleById = useMemo(() => {
        const m = new Map();
        if (!sample) nodePeople.forEach(p => m.set(p.id, p));
        (src?.participants ?? []).forEach(p => m.set(p.id, { ...m.get(p.id), ...p }));
        return m;
    }, [sample, nodePeople, src]);

    const membersOf = useMemo(() => new Map(groups.map(g => [g.id, new Set(src?.memberships?.[String(g.id)] ?? [])])), [groups, src]);

    const pool = useMemo(() => {
        const inAny = new Set(groups.flatMap(g => [...membersOf.get(g.id)]));
        if (poolMode === 'everyone' && !sample) {
            return [...new Set([...nodePeople.map(p => p.id), ...inAny])];
        }
        return [...inAny].filter(id => peopleById.has(id));
    }, [poolMode, sample, nodePeople, groups, membersOf, peopleById]);

    // --- plans: one per breakout round ---------------------------------------
    // Keyed on content, not identity: a reload returning the same memberships
    // shouldn't reshuffle anything.
    const breakouts = flow.filter(s => s.kind === 'breakout');
    const membershipSig = groups.map(g => `${g.id}:${[...membersOf.get(g.id)].sort().join(',')}`).join(';');
    const sortedPool = useMemo(() => [...pool].sort((a, b) => a - b), [pool]);
    const poolSig = sortedPool.join(',');
    const mixSig = JSON.stringify(seats);
    const seedsSig = breakouts.map(s => `${s.key}:${s.seed}`).join(',');
    const planArgs = useMemo(
        () => ({ pool: sortedPool, groups, membersOf, size, mix: seats }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [poolSig, membershipSig, size, mixSig],
    );
    const plans = useMemo(
        () => Object.fromEntries(breakouts.map(s => [s.key, planRooms({ ...planArgs, seed: s.seed })])),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [planArgs, seedsSig],
    );

    const topicTexts = (src?.topics ?? []).map(t => t.text);
    const publishedByStep = useMemo(() => {
        const byStep = {};
        (src?.rooms || []).forEach(r => { (byStep[r.step_id] = byStep[r.step_id] || []).push(r); });
        return Object.fromEntries(Object.entries(byStep).map(([id, rs]) => [id, fromServerRooms(rs)]));
    }, [src]);

    // Rounds in flow order, each with its rooms, topic offset (topics carry on
    // across rounds) and how many pairs meet again from earlier rounds.
    const rounds = [];
    let seen = new Set();
    let topicOffset = 0;
    breakouts.forEach((step, r) => {
        const rooms = drafts[step.key] ?? plans[step.key]?.rooms ?? [];
        const published = step.id != null ? publishedByStep[step.id] : undefined;
        const upToDate = !!published
            && sameRooms(rooms, published)
            && published.every((room, i) => room.topic === topicFor(topicOffset + i, topicTexts));
        rounds.push({ step, index: r, rooms, topicOffset, seenBefore: seen, repeats: r ? countRepeats(rooms, seen) : 0, upToDate, published });
        seen = new Set([...seen, ...roomPairs(rooms)]);
        topicOffset += rooms.length;
    });
    const totalRooms = topicOffset;
    const isPublished = !!src?.published_at;
    const allUpToDate = isPublished && rounds.length > 0 && rounds.every(r => r.upToDate);

    const setRoundDraft = (key, rooms) => setDrafts(d => {
        const next = { ...d };
        if (rooms) next[key] = rooms; else delete next[key];
        return next;
    });
    const moveMember = round => (personId, from, to) => {
        const next = round.rooms.map(r => ({ ...r, personIds: [...r.personIds] }));
        if (from !== TRAY) next[from].personIds = next[from].personIds.filter(id => id !== personId);
        if (to !== TRAY) next[to].personIds.push(personId);
        setRoundDraft(round.step.key, next);
    };

    // Any change to the ideal room replans every round from scratch.
    const changeSize = v => { setSize(v); setDrafts({}); queueSettings({ room_size: v }); };
    const changePool = v => { setPoolMode(v); setDrafts({}); queueSettings({ pool_mode: v }); };
    const changeSeats = (gid, v) => { setSeats(s => ({ ...s, [gid]: v })); setDrafts({}); queueSeats(gid, v); };
    // Round 1 takes any new seed; later rounds pick one that keeps apart
    // people who've already met.
    const reshuffle = round => {
        const seed = round.index === 0
            ? newSeed()
            : pickSeed({ planArgs, seen: round.seenBefore, groups, membersOf });
        setRoundDraft(round.step.key, null);
        changeFlow(flow.map(s => (s.key === round.step.key ? { ...s, seed } : s)));
    };

    const publish = async () => {
        setPublishing(true);
        try {
            const saved = await saveFlowNow();
            const ids = saved ? saved.flow.map(s => s.id) : flow.map(s => s.id);
            const steps = flow
                .map((s, i) => ({ s, id: ids[i] }))
                .filter(x => x.s.kind === 'breakout')
                .map(x => ({
                    step_id: x.id,
                    rooms: rounds.find(r => r.step.key === x.s.key).rooms.map(r => ({ person_ids: r.personIds, quota: r.quota })),
                }));
            const d = await admin.publish(steps);
            const byStep = {};
            d.rooms.forEach(r => { (byStep[r.step_id] = byStep[r.step_id] || []).push(r); });
            setDrafts(Object.fromEntries(flow.map((s, i) => [s.key, byStep[ids[i]]]).filter(([, rs]) => rs).map(([k, rs]) => [k, fromServerRooms(rs)])));
        } catch { /* the hook already said what went wrong */ }
        setPublishing(false);
    };
    const unpublish = () => admin.unpublish().then(() => setDrafts({})).catch(() => {});

    // --- running the flow ----------------------------------------------------
    const startedAt = sample ? sampleStartedAt : parseServerTime(src?.flow_started_at);
    const pausedAt = sample ? samplePausedAt : parseServerTime(src?.flow_paused_at);
    const running = startedAt != null;
    const paused = running && pausedAt != null;
    // Minutes added during the run come from the server (or the sample run),
    // on top of the locally edited flow.
    const serverExtended = useMemo(() => new Map((src?.flow ?? []).map(s => [s.id, s.extended_minutes || 0])), [src]);
    const timedFlow = flow.map(s => ({
        ...s,
        extended_minutes: sample ? (sampleExtended[s.key] || 0) : (serverExtended.get(s.id) || 0),
    }));
    const now = useServerClock(sample ? null : src?.server_now, running && !paused);
    // A paused flow is frozen at the moment it was paused.
    const position = flowPosition(timedFlow, startedAt, paused ? pausedAt : now);
    const liveStep = position && !position.finished ? flow[position.index] : null;
    const resetSampleRun = () => { setSamplePausedAt(null); setSampleExtended({}); };
    const startFlow = () => (sample
        ? (resetSampleRun(), setSampleStartedAt(Date.now()))
        : saveFlowNow().then(() => admin.startFlow()).catch(() => {}));
    const stopFlow = () => (sample ? (resetSampleRun(), setSampleStartedAt(null)) : admin.stopFlow().catch(() => {}));
    const pauseFlow = () => (sample ? setSamplePausedAt(Date.now()) : admin.pauseFlow().catch(() => {}));
    // Resuming moves the start forward by the pause, so elapsed time skips it
    // (the server does the same for a live run).
    const resumeFlow = () => {
        if (!sample) { admin.resumeFlow().catch(() => {}); return; }
        setSampleStartedAt(s => s + (Date.now() - samplePausedAt));
        setSamplePausedAt(null);
    };
    // End the live step now. Pulling the start back by the step's remaining
    // time lands everyone at the start of the next step (the server does the
    // same for a live run); on the last step it finishes the flow.
    const nextStep = () => {
        if (!liveStep) return;
        if (!sample) { admin.nextStep(liveStep.id).catch(() => {}); return; }
        setSampleStartedAt(s => s - position.remainingMs);
    };
    const extendStep = minutes => {
        if (!liveStep) return;
        if (sample) setSampleExtended(e => ({ ...e, [liveStep.key]: (e[liveStep.key] || 0) + minutes }));
        else admin.extendStep(liveStep.id, minutes).catch(() => {});
    };
    const startHint = sample ? 'Runs the sample flow in this browser only'
        : rounds.length && !isPublished ? 'Publish the rooms first'
            : rounds.length && !allUpToDate ? 'Starts with the published rooms — you have unpublished changes'
                : 'Everyone sees the live step and a countdown';

    const sampleSwitch = (
        <Space size={6}>
            <ExperimentOutlined style={{ color: 'var(--text-tertiary)' }} />
            <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>Sample data</Text>
            <Switch size="small" checked={sample} onChange={toggleSample} />
        </Space>
    );

    if (!sample && admin.loading) return <div className="gathering-empty"><Spin /></div>;
    // Keep the sample switch reachable when the live load fails — the local
    // dev database drops connections often enough that this matters.
    if (!sample && (admin.error || !src)) {
        return (
            <>
                <div className="gathering-admin-bar"><span />{sampleSwitch}</div>
                <Alert
                    type="error"
                    showIcon
                    title="Couldn't load the gathering"
                    description={admin.error}
                    action={<Button size="small" onClick={() => admin.refresh()}>Retry</Button>}
                />
            </>
        );
    }

    // Local stand-ins for the topic API while in sample mode.
    const sampleTopic = {
        create: text => setSampleTopics(ts => [...ts, { id: `t${Date.now().toString(36)}${ts.length}`, text }]),
        update: (id, text) => setSampleTopics(ts => ts.map(t => (t.id === id ? { ...t, text } : t))),
        remove: id => setSampleTopics(ts => ts.filter(t => t.id !== id)),
        reorder: ids => setSampleTopics(ts => ids.map(id => ts.find(t => t.id === id)).filter(Boolean)),
    };

    const colorOf = gid => paletteColor(groups.find(g => g.id === gid)?.color ?? 0);

    // Export what's on screen (sample or live); import only into the live
    // node, and not mid-run. After an import, re-read everything from the
    // server rather than keeping local edits made against the old setup.
    const buildConfig = () => buildGatheringConfig({
        roomSize: size, poolMode, groups, seats, topics: src.topics ?? [], flow, nodeName: sample ? 'sample' : nodeName,
    });
    const importBlocked = sample ? 'Turn off sample data to import into this node'
        : running ? 'Stop the flow before importing' : null;
    const importConfig = config => {
        loadedFor.current = null;
        return admin.importConfig(config).then(() => setDrafts({}));
    };
    // What members get (no gathering / waiting room / open) — saved straight
    // away, not debounced: it's the one control members notice.
    const setStatus = status => {
        setStatusSaving(true);
        admin.updateSettings({ status }).catch(() => {}).finally(() => setStatusSaving(false));
    };
    const setClosedMessage = closedMessage => admin.updateSettings({ closed_message: closedMessage }).catch(() => {});
    // patch: { guest_mode?, guest_passcode? } — saved straight away, like status.
    const setGuestAccess = patch => admin.updateSettings(patch).catch(() => {});
    const removeGuests = scope => admin.removeGuests(scope).catch(() => {});
    // Picks arrive from members' screens and the admin view doesn't poll, so
    // pull them in on demand. Only the server's data reloads — settings and
    // room drafts stay as they are (loadedFor keeps them local).
    const refreshGroups = () => {
        setRefreshingGroups(true);
        admin.refresh().finally(() => setRefreshingGroups(false));
    };
    // Stale picks from an earlier gathering: clear them, keep the groups.
    const clearPicks = () => {
        loadedFor.current = null;
        return admin.clearPicks().then(() => setDrafts({})).catch(() => {});
    };
    const startOver = () => {
        loadedFor.current = null;
        return admin.resetGathering().then(() => { setDrafts({}); setScratch(false); }).catch(() => {});
    };
    // The sample setup as a config, for "start from the sample".
    const sampleConfig = buildGatheringConfig({
        roomSize: SAMPLE_SOURCE.room_size, poolMode: SAMPLE_SOURCE.pool_mode, groups: SAMPLE_GROUPS, seats: {},
        topics: SAMPLE_TOPICS, flow: SAMPLE_FLOW, nodeName: 'sample',
    });
    // A never-set-up live gathering opens on the choice of how to start —
    // but only while it's still closed. Once the admin moves it to the
    // waiting room or open, they've already decided to proceed with just
    // the single-room setup, so the chooser no longer applies.
    const isBlank = !sample && isBlankSetup(src);
    const choosing = isBlank && !scratch && src.status === 'closed';
    // Blank and past closed: a single common room, so the flow editors stay
    // out of the way until the admin chooses to set one up.
    const singleRoomOnly = isBlank && !scratch && src.status !== 'closed';
    const participantCount = new Set(groups.flatMap(g => [...membersOf.get(g.id)])).size;
    const firstPlan = rounds.length ? plans[rounds[0].step.key] : null;
    const shownRound = rounds.find(r => r.step.key === activeRound) ?? rounds.find(r => r.step === liveStep) ?? rounds[0];

    const roundPane = round => {
        const seated = new Set(round.rooms.flatMap(r => r.personIds));
        const unseated = pool.filter(id => !seated.has(id));
        const met = round.rooms.filter(r => describeRoom(r, groups, membersOf).met).length;
        const drafted = drafts[round.step.key] !== undefined;
        return (
            <>
                <div className="gathering-round-bar">
                    <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>
                        {round.rooms.length} rooms · {met}/{round.rooms.length} meet the mix
                        {round.index > 0 && (
                            <Tooltip title="Pairs of people who already shared a room in an earlier round. Reshuffle picks an arrangement that keeps this low.">
                                {' · '}<Text type={round.repeats ? 'warning' : 'success'} style={{ fontSize: 'var(--fs-sm)' }}>
                                    {round.repeats} {round.repeats === 1 ? 'pair meets' : 'pairs meet'} again
                                </Text>
                            </Tooltip>
                        )}
                    </Text>
                    <Space size={6}>
                        {drafted && !round.upToDate && (
                            <Tooltip title={round.published ? 'Back to the published rooms' : 'Back to the plan'}>
                                <Button size="small" icon={<UndoOutlined />} onClick={() => setRoundDraft(round.step.key, round.published ?? null)}>
                                    Undo
                                </Button>
                            </Tooltip>
                        )}
                        <Tooltip title={round.index === 0 ? `Shuffle #${round.step.seed}` : 'Reshuffle, keeping apart people who met in earlier rounds'}>
                            <Button size="small" icon={<ReloadOutlined />} onClick={() => reshuffle(round)} disabled={pool.length === 0 || running}>
                                Reshuffle
                            </Button>
                        </Tooltip>
                    </Space>
                </div>
                <RoomBoard
                    rooms={round.rooms}
                    unseated={unseated}
                    groups={groups}
                    colorOf={colorOf}
                    membersOf={membersOf}
                    peopleById={peopleById}
                    topics={topicTexts}
                    topicOffset={round.topicOffset}
                    highlight={highlight}
                    onMove={moveMember(round)}
                    onJoinRoom={(room, r) => navigate(gatheringCallUrl(room.hangoutRoomName, `Round ${round.index + 1} · Room ${r + 1}`))}
                />
            </>
        );
    };

    return (
        <>
            <GatheringStatus
                status={sample ? 'open' : src.status}
                message={sample ? null : src.closed_message}
                onStatus={setStatus}
                onMessage={setClosedMessage}
                running={running}
                sample={sample}
                saving={statusSaving}
            />
            {featureGatheringGuestAccess && (
                <GuestAccess
                    mode={sample ? 'off' : src.guest_mode}
                    passcode={sample ? null : src.guest_passcode}
                    onChange={setGuestAccess}
                    guests={sample ? null : src.guests}
                    onRemoveGuests={removeGuests}
                    sample={sample}
                />
            )}

            <div className="gathering-admin-bar">
                <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>
                    {participantCount} {participantCount === 1 ? 'person has' : 'people have'} picked a group
                </Text>
                <Space size={16} wrap>
                    {!sample && !choosing && (
                        <Popconfirm
                            title="Start over?"
                            description="Clears groups and everyone's picks, topics, the flow and published rooms."
                            okText="Start over"
                            okButtonProps={{ danger: true }}
                            onConfirm={startOver}
                            disabled={running}
                        >
                            <Tooltip title={running ? 'Stop the flow before starting over' : 'Design from scratch, import, or start from the sample'}>
                                <Button size="small" icon={<ClearOutlined />} disabled={running}>Start over</Button>
                            </Tooltip>
                        </Popconfirm>
                    )}
                    <ConfigShare
                        buildConfig={buildConfig}
                        currentGroups={sample ? [] : groups}
                        importBlocked={importBlocked}
                        paletteColor={paletteColor}
                        onImport={importConfig}
                    />
                    {sampleSwitch}
                </Space>
            </div>

            {(singleRoomOnly || (isBlank && src.status === 'open')) && (
                // Nothing set up = a single common room (pods/gatherings
                // is_single_room): opened, members get one Join button.
                <Alert
                    className="gathering-section"
                    type={src.status === 'open' ? 'success' : 'info'}
                    showIcon
                    title="Single common room"
                    description={`${src.status === 'open'
                        ? 'Everyone who comes in joins one video room.'
                        : 'Once you open the gathering, everyone who comes in joins one video room.'} Set up a flow ${singleRoomOnly ? '' : 'below '}to split people into breakout rooms instead.`}
                    action={(
                        <Space>
                            {src.status === 'open' && src.common_hangout_room_name && (
                                <Button
                                    size="small"
                                    icon={<VideoCameraOutlined />}
                                    onClick={() => navigate(gatheringCallUrl(src.common_hangout_room_name, 'Gathering'))}
                                >
                                    Join
                                </Button>
                            )}
                            {singleRoomOnly && <Button size="small" onClick={() => setScratch(true)}>Set up a flow</Button>}
                        </Space>
                    )}
                />
            )}
            {choosing ? (
                <StartChooser
                    paletteColor={paletteColor}
                    sampleConfig={sampleConfig}
                    onScratch={() => setScratch(true)}
                    onImport={importConfig}
                />
            ) : singleRoomOnly ? null : (
                <>
                <Section
                    step={1}
                    title="Flow"
                    extra={running && (paused
                        ? <Tag color="warning" style={{ margin: 0 }}>Paused</Tag>
                        : <Tag color="processing" style={{ margin: 0 }}>Live</Tag>)}
                >
                    <FlowEditor
                        steps={timedFlow}
                        onChange={changeFlow}
                        position={position}
                        startedAt={startedAt}
                        running={running}
                        paused={paused}
                        canStart={sample || !rounds.length || isPublished}
                        startHint={startHint}
                        onStart={startFlow}
                        onStop={stopFlow}
                        onPause={pauseFlow}
                        onResume={resumeFlow}
                        onExtend={extendStep}
                        onNext={nextStep}
                        commonRoomName={sample ? null : src?.common_hangout_room_name}
                        onJoinCommonRoom={() => navigate(gatheringCallUrl(src.common_hangout_room_name, 'Common room'))}
                    />
                </Section>

                <Section
                    step={2}
                    title="Groups"
                    extra={sample ? <Tag>sample — changes aren't saved</Tag> : (
                        <Space size={8} wrap>
                            <Popconfirm
                                title="Clear everyone's picks?"
                                description="The groups stay; everyone picks again. Published rooms aren't changed until you republish."
                                okText="Clear picks"
                                okButtonProps={{ danger: true }}
                                onConfirm={clearPicks}
                                disabled={running || participantCount === 0}
                            >
                                <Tooltip title={running ? 'Stop the flow before clearing picks'
                                    : participantCount === 0 ? 'Nobody has picked a group' : 'Make everyone pick their groups again — e.g. picks left over from an earlier gathering'}>
                                    <Button size="small" icon={<UserDeleteOutlined />} disabled={running || participantCount === 0}>Clear picks</Button>
                                </Tooltip>
                            </Popconfirm>
                            <Tooltip title="Load the latest picks — people pick on their own screens">
                                <Button size="small" icon={<ReloadOutlined />} loading={refreshingGroups} onClick={refreshGroups}>
                                    Refresh
                                </Button>
                            </Tooltip>
                        </Space>
                    )}
                >
                    <GroupEditor
                        groups={groups}
                        colorOf={colorOf}
                        membersOf={membersOf}
                        peopleById={peopleById}
                        readOnly={sample}
                        onCreate={name => admin.createGroup({ name }).catch(() => {})}
                        onRename={(id, name) => updateGroup(id, { name }).catch(() => {})}
                        onDelete={id => admin.deleteGroup(id).catch(() => {})}
                    />
                    {participantCount > 0 && (
                        <GroupOverlap groups={groups} colorOf={colorOf} membersOf={membersOf} pool={pool} peopleById={peopleById} />
                    )}
                </Section>

                <Section step={3} title="Ideal room">
                    {groups.length === 0 ? (
                        <Text type="secondary">Add a group first — the ideal room says how many seats each group gets.</Text>
                    ) : (
                        <IdealRoom
                            groups={groups}
                            colorOf={colorOf}
                            size={size}
                            mix={seats}
                            onSize={changeSize}
                            onMix={changeSeats}
                            demand={firstPlan?.demand ?? {}}
                            supply={firstPlan?.supply ?? {}}
                            roomCount={rounds[0]?.rooms.length ?? 0}
                        />
                    )}
                </Section>

                <Section
                    step={4}
                    title="Topics"
                    extra={<Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>Handed to rooms in order, round after round</Text>}
                >
                    <TopicEditor
                        topics={src.topics ?? []}
                        roomCount={totalRooms}
                        roundCount={rounds.length}
                        onCreate={text => (sample ? Promise.resolve(sampleTopic.create(text)) : admin.createTopic(text))}
                        onUpdate={(id, text) => (sample ? sampleTopic.update(id, text) : admin.updateTopic(id, text).catch(() => {}))}
                        onDelete={id => (sample ? sampleTopic.remove(id) : admin.deleteTopic(id).catch(() => {}))}
                        onReorder={ids => (sample ? sampleTopic.reorder(ids) : admin.reorderTopics(ids).catch(() => {}))}
                    />
                </Section>

                <Section
                    step={5}
                    title="Rooms"
                    extra={(
                        <Space wrap>
                            {isPublished && !sample && (
                                <Popconfirm
                                    title="Unpublish rooms?"
                                    description="Members will no longer see their rooms, and a running flow stops."
                                    okText="Unpublish"
                                    onConfirm={unpublish}
                                >
                                    <Button size="small" icon={<StopOutlined />}>Unpublish</Button>
                                </Popconfirm>
                            )}
                            <Tooltip title={sample ? 'Sample data can’t be published' : allUpToDate ? 'Members already see these rooms' : 'Publishes every round — members see their room in each'}>
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<SendOutlined />}
                                    onClick={publish}
                                    loading={publishing}
                                    disabled={sample || allUpToDate || totalRooms === 0}
                                >
                                    {isPublished ? 'Publish changes' : 'Publish rooms'}
                                </Button>
                            </Tooltip>
                        </Space>
                    )}
                >
                    <div className="gathering-rooms-toolbar">
                        <Segmented
                            size="small"
                            value={poolMode}
                            onChange={changePool}
                            options={[
                                { label: 'People who picked a group', value: 'groups' },
                                { label: 'Everyone', value: 'everyone' },
                            ]}
                        />
                        {!sample && poolMode === 'everyone' && (
                            <Space size={6}>
                                <Switch size="small" checked={includePending} onChange={v => { setIncludePending(v); setDrafts({}); }} />
                                <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>Include people without an account</Text>
                            </Space>
                        )}
                        <span className="gathering-publish-status">
                            {sample ? null : allUpToDate ? (
                                <Tag color="success" style={{ margin: 0 }}>Published {publishedLabel(src.published_at)}</Tag>
                            ) : isPublished ? (
                                <Tag color="warning" style={{ margin: 0 }}>Changes not published</Tag>
                            ) : (
                                <Tag style={{ margin: 0 }}>Draft — members don't see rooms yet</Tag>
                            )}
                        </span>
                    </div>
                    {totalRooms > 0 && (
                        <div className="gathering-highlight">
                            <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>{pool.length} people · highlight:</Text>
                            {groups.map(g => (
                                <Tag.CheckableTag
                                    key={g.id}
                                    checked={highlight === g.id}
                                    onChange={on => setHighlight(on ? g.id : null)}
                                >
                                    <span className="gathering-dot" style={{ background: colorOf(g.id), marginInlineEnd: 6 }} />
                                    {g.name}
                                </Tag.CheckableTag>
                            ))}
                            <Text type="secondary" style={{ fontSize: 'var(--fs-xs)', marginInlineStart: 'auto' }}>
                                Drag people between rooms to adjust
                            </Text>
                        </div>
                    )}
                    {peopleLoading && poolMode === 'everyone' && !sample ? (
                        <div className="gathering-empty"><Spin /></div>
                    ) : rounds.length === 0 ? (
                        <Empty className="gathering-empty" image={Empty.PRESENTED_IMAGE_SIMPLE} description="This flow has no breakout rounds — add one above." />
                    ) : pool.length === 0 ? (
                        <Empty
                            className="gathering-empty"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={groups.length === 0
                                ? 'Add groups, then share this page so people can pick theirs — or switch on sample data'
                                : 'Nobody has picked a group yet. Share this page so people can join.'}
                        />
                    ) : (
                        <Tabs
                            activeKey={shownRound.step.key}
                            onChange={setActiveRound}
                            items={rounds.map(round => ({
                                key: round.step.key,
                                label: (
                                    <span className="gathering-round-tab">
                                        {round.step === liveStep && <span className="gathering-live-dot" />}
                                        Round {round.index + 1}
                                        <Text type="secondary" style={{ fontSize: 'var(--fs-xs)' }}>{round.step.duration_minutes}′</Text>
                                    </span>
                                ),
                                children: roundPane(round),
                            }))}
                        />
                    )}
                </Section>
                </>
            )}
        </>
    );
}

export default AdminView;
